import {
    Stack,
    Input,
    Chip,
    Typography,
    Grid,
    Card,
    Box,
    ListItem,
    List,
    ListItemDecorator,
    ListDivider
} from "@mui/joy";
import React, {useEffect, useState} from "react";
import {CATEGORIES_MAP} from "../models/category";
import {resources} from "../values/mockResources";
import ResourceItemComponent from "../components/Resource/ResourceItemComponent";
import {BookOpen, Search} from "lucide-react";
import {ResourceType, resourceTypeIconMap} from "../models/resource";
import RecentlyViewedResourceComponent from "../components/Resource/RecentlyViewedResourceComponent";
import {useAsync} from "../hooks/useAsyncResult";
import {getResources} from "../services/api/resource";
import LoadingComponent from "../components/Loading/LoadingComponent";
import LoadingPageComponent from "../components/Loading/LoadingPageComponent";
import ResourceLibraryComponent from "../components/Resource/ResourceLibraryComponent";
import {useSearchParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useSubscription} from "../hooks/useSubscription";
import LoadingError from "../components/Error/LoadingError";

const ResourcePage = () => {
    const [searchParams, setSearchParams] = useSearchParams()
    const [libraryVisible, setLibraryVisible] = useState(false)
    const resourcesAsyncResult = useAsync(getResources, true)
    const {t} = useTranslation()

    const loadResources = () => {
        if (!searchParams.get("view"))
            resourcesAsyncResult.execute()
    }

    useEffect(() => {
        loadResources()
    }, [])

    useEffect(() => {
        setLibraryVisible(!!searchParams.get("view"))
    }, [searchParams])

    const handleShowLibrary = (type: ResourceType) => {
        const urlSearchParams = new URLSearchParams(searchParams)
        urlSearchParams.set("type", type)
        urlSearchParams.set("view", "library")
        setSearchParams(urlSearchParams)
    }

    return <Stack padding={3} gap={4} boxSizing={"border-box"} sx={{height: "100dvh"}}>

        <Stack gap={1}>
            <Typography level={"h2"}>
                {t("resourceLabel")}
            </Typography>
            <Typography>
                {t("resourceDescription")}
            </Typography>
        </Stack>

        {!resourcesAsyncResult.isSuccess && !resourcesAsyncResult.loading &&
            <LoadingError label={t("resourceLabel")} onRetry={loadResources}/>}

        {(resourcesAsyncResult.loading) ?
            <LoadingPageComponent label={t("resourceLabel")}/> :
            resourcesAsyncResult.payload && <>
                <Input size={"lg"} startDecorator={<Search/>} placeholder={t("searchResourcePlaceholder")}/>

                <Stack gap={4}>

                    {resourcesAsyncResult.payload?.resourceInfo &&
                        <Grid container spacing={3}>
                            {Object.keys(resourcesAsyncResult.payload.resourceInfo).map((value, index) =>
                                <Grid xs={12} sm={6} md={4}>
                                    <Card sx={{padding: 3, cursor: "pointer"}}
                                          onClick={() => handleShowLibrary(value as ResourceType)}>
                                        <Stack gap={2}>
                                            <Box data-accent width={"max-content"} display={"flex"}
                                                 alignItems="center"
                                                 padding={1} height={"max-content"} sx={{
                                                bgcolor: "primary.softBg",
                                                color: "primary.softColor"
                                            }} borderRadius={10}>
                                                {resourceTypeIconMap[value]}
                                            </Box>

                                            <Stack>
                                                <Typography textTransform={"capitalize"} level={"title-lg"}>
                                                    {value}
                                                </Typography>
                                                <Typography>
                                                    {resourcesAsyncResult.payload.resourceInfo[value]} resources
                                                </Typography>
                                            </Stack>

                                        </Stack>
                                    </Card>
                                </Grid>)}
                        </Grid>}

                    <Grid container spacing={3}>

                        {resourcesAsyncResult.payload?.resources &&
                            <Grid container spacing={3} xs={12}
                                  lg={resourcesAsyncResult.payload?.recentResources?.length > 0 ? 8 : 12}>
                                <Grid xs={12}>
                                    <Typography level={"h3"}>
                                        {t("recentResourcesLabel")}
                                    </Typography>
                                </Grid>
                                {resourcesAsyncResult.payload.resources.map((value, index) =>
                                    <Grid xs={12}
                                          lg={resourcesAsyncResult.payload?.recentResources?.length > 0 ? 6 : 12}>
                                        <ResourceItemComponent resource={value}/>
                                    </Grid>)}
                            </Grid>}

                        {resourcesAsyncResult.payload?.recentResources &&
                            <Grid xs={12} lg={4}>
                                <Stack gap={3}>
                                    <Typography>
                                        {t("recentlyViewedResourcesLabel")}
                                    </Typography>
                                    <Card data-no-glow data-no-animation sx={{padding: 3}}>
                                        <List variant="plain" sx={{minWidth: 240, borderRadius: 'sm'}}>
                                            {resourcesAsyncResult.payload?.recentResources.map((value, index, array) =>
                                                <>
                                                    <ListItem>
                                                        <RecentlyViewedResourceComponent resourceLabel={value.title}
                                                                                         date={value.date}/>
                                                    </ListItem>
                                                    {index !== array.length - 1 && <ListDivider inset={"gutter"}/>}
                                                </>)}
                                        </List>
                                    </Card>

                                </Stack>
                            </Grid>}
                    </Grid>


                </Stack>
            </>}

        <ResourceLibraryComponent resourceInfo={resourcesAsyncResult.payload?.resourceInfo}
                                  open={libraryVisible}/>

    </Stack>
}

export default ResourcePage